import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { FileData, FilePayload } from '@/libs/fleet-api/services/types';
import { Alert, Col, Form, Row, Upload } from 'antd';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { FC, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import {
	SubmitMPPSlikRequestFilesPayload,
	useSubmitMPPSlikRequestFilesMutation,
} from '@/libs/fleet-api/services/mpp';
import { useModals } from '@/components/modals/ModalProvider';

export const useUploadSLIKModal = () =>
	useAsyncModal<
		{
			customerId: string;
			slikFormFile?: FileData;
			applicationLetterFile?: FileData;
		},
		boolean
	>();

export type UploadSLIKModalProps = ReturnType<typeof useUploadSLIKModal>;

const UploadSLIKModal: FC<UploadSLIKModalProps> = ({ modal, close, data }) => {
	const { openPreviewModal } = useModals();
	const { form, fieldName } = useForm<SubmitMPPSlikRequestFilesPayload>();

	const submitMPPSlikRequestFilesMutation =
		useSubmitMPPSlikRequestFilesMutation();

	const uploadSLIKMutation = useMutation({
		mutationFn: uploader,
	});

	const uploadApplicationLetterMutation = useMutation({
		mutationFn: uploader,
	});

	const slikFormFile = data?.slikFormFile;
	const applicationLetterFile = data?.applicationLetterFile;
	const [hasBothFiles, setHasBothFiles] = useState(
		Boolean(slikFormFile?.id && applicationLetterFile?.id),
	);

	// Initialize form fields with existing data
	useEffect(() => {
		if (slikFormFile || applicationLetterFile) {
			const initialValues: Partial<SubmitMPPSlikRequestFilesPayload> = {};

			if (slikFormFile) {
				initialValues.slikFormFile = {
					id: slikFormFile.id,
					name: slikFormFile.name,
					path: slikFormFile.path,
				};
			}

			if (applicationLetterFile) {
				initialValues.applicationLetterFile = {
					id: applicationLetterFile.id,
					name: applicationLetterFile.name,
					path: applicationLetterFile.path,
				};
			}

			if (Object.keys(initialValues).length > 0) {
				form.setFieldsValue(initialValues);
			}
		}

		// Update hasBothFiles state based on initial data
		setHasBothFiles(Boolean(slikFormFile?.id && applicationLetterFile?.id));
	}, [slikFormFile, applicationLetterFile, form]);

	// Function to check current file status
	const updateFileStatus = () => {
		const currentSlikForm =
			form.getFieldValue(fieldName('slikFormFile')) || slikFormFile;
		const currentApplicationLetter =
			form.getFieldValue(fieldName('applicationLetterFile')) ||
			applicationLetterFile;
		setHasBothFiles(Boolean(currentSlikForm?.id && currentApplicationLetter?.id));
	};

	const handleFRMRequest = async () => {
		try {
			// Get current files from form or initial data
			const currentSlikForm =
				form.getFieldValue(fieldName('slikFormFile')) || slikFormFile;
			const currentApplicationLetter =
				form.getFieldValue(fieldName('applicationLetterFile')) ||
				applicationLetterFile;

			// Check if both files are available
			if (!currentSlikForm?.id || !currentApplicationLetter?.id) {
				throw new Error('Dokumen FAPP dan PDP wajib diisi');
			}

			// Prepare payload with FAPP and PDP files
			const payload = {
				customerId: data!.customerId,
				slikFormFile: {
					id: currentSlikForm.id || '',
					name: currentSlikForm.name,
					path: currentSlikForm.url || currentSlikForm.path, // Use URL as path if available
				} as FilePayload,
				applicationLetterFile: {
					id: currentApplicationLetter.id || '',
					name: currentApplicationLetter.name,
					path: currentApplicationLetter.url || currentApplicationLetter.path, // Use URL as path if available
				} as FilePayload,
			};

			await submitMPPSlikRequestFilesMutation.mutateAsync(payload);

			// After successful submission, close the modal
			close(true);
		} catch (error) {
			console.error('FRM SLIK request failed:', error);
		}
	};

	return (
		<Modal
			{...modal}
			width={940}
			buttonPosition="end"
			cancelText="Cancel"
			okText="Request SLIK"
			okButtonProps={{
				disabled: !hasBothFiles || submitMPPSlikRequestFilesMutation.isPending,
				loading: submitMPPSlikRequestFilesMutation.isPending,
			}}
			onCancel={() => close()}
			onOk={handleFRMRequest}
			afterOpenChange={() => form.resetFields()}
		>
			<div style={{ marginBottom: 20 }}>
				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Request SLIK - Upload Dokumen FAPP & PDP</FleetTitle>
				</div>
				<div style={{ marginBottom: 20 }}>
					<Alert
						message={
							<span style={{ color: '#1557BC' }}>
								Upload dokumen FAPP dan PDP untuk melanjutkan request SLIK.
								Kedua dokumen wajib diisi sebelum dapat melanjutkan.
							</span>
						}
						type={hasBothFiles ? 'success' : 'info'}
					/>
				</div>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={async (payload) => {
						// This form submission is not used for FRM - we use handleFRMRequest instead
						console.log('Form submitted with payload:', payload);
					}}
				>
					<Row gutter={40}>
						<Col span={12}>
							{/* File preview link for SLIK Form */}
							{slikFormFile && (
								<div style={{ marginBottom: 16 }}>
									<Form.Item label="View FAPP">
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												openPreviewModal(slikFormFile);
											}}
											style={{ color: '#1890ff' }}
										>
											{slikFormFile.name}
										</a>
									</Form.Item>
								</div>
							)}

							<Form.Item
								name={fieldName('slikFormFile')}
								label={
									slikFormFile ? 'Re-upload Form FAPP' : 'Upload Form FAPP'
								}
								labelCol={{
									span: 12,
									style: {
										alignSelf: 'center',
									},
								}}
								rules={[rule.required()]}
							>
								<Upload.Dragger
									listType="picture"
									maxCount={1}
									accept=".jpg,.jpeg,.png,.pdf"
									beforeUpload={() => false}
									fileList={
										form.getFieldValue(fieldName('slikFormFile'))
											? [
													{
														uid:
															form.getFieldValue(fieldName('slikFormFile'))
																?.id || '1',
														name:
															form.getFieldValue(fieldName('slikFormFile'))
																?.name || '',
														status: 'done' as const,
														url: slikFormFile ? '#' : undefined,
													},
												]
											: []
									}
									showUploadList={{
										showRemoveIcon: true,
										showPreviewIcon: true,
									}}
									// eslint-disable-next-line @typescript-eslint/no-unused-vars
									onPreview={(_file) => {
										if (slikFormFile) {
											openPreviewModal(slikFormFile);
										}
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${data?.customerId}/fapp-form`;

										try {
											await uploadSLIKMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(fieldName('slikFormFile'), {
												id: fileid,
												name: file.name,
												path: filepath,
											});
											updateFileStatus();
										} catch {
											form.setFieldValue(fieldName('slikFormFile'), null);
											updateFileStatus();
										}
									}}
									onRemove={() => {
										form.setFieldValue(fieldName('slikFormFile'), null);
										updateFileStatus();
										return false;
									}}
								>
									{uploadSLIKMutation.isPending ? (
										<LoadingOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									) : (
										<>
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
											<div
												style={{
													fontSize: 12,
													marginTop: 10,
													color: '#BABABA',
												}}
											>
												{form.getFieldValue(fieldName('slikFormFile'))
													? 'Upload to replace current file'
													: slikFormFile
														? 'Upload to replace current file'
														: 'Silakan Upload Form FAPP Anda'}
											</div>
										</>
									)}
								</Upload.Dragger>
							</Form.Item>
						</Col>
						<Col span={12}>
							{/* File preview link for Application Letter */}
							{applicationLetterFile && (
								<div style={{ marginBottom: 16 }}>
									<Form.Item label="View PDP">
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												openPreviewModal(applicationLetterFile);
											}}
											style={{ color: '#1890ff' }}
										>
											{applicationLetterFile.name}
										</a>
									</Form.Item>
								</div>
							)}

							<Form.Item
								name={fieldName('applicationLetterFile')}
								label={
									applicationLetterFile
										? 'Re-upload Surat Permohonan'
										: 'Upload Surat Permohonan'
								}
								labelCol={{
									span: 12,
									style: {
										alignSelf: 'center',
									},
								}}
								rules={[rule.required()]}
							>
								<Upload.Dragger
									maxCount={1}
									listType="picture"
									accept=".jpg,.jpeg,.png,.pdf"
									beforeUpload={() => false}
									fileList={
										form.getFieldValue(fieldName('applicationLetterFile'))
											? [
													{
														uid:
															form.getFieldValue(
																fieldName('applicationLetterFile'),
															)?.id || '1',
														name:
															form.getFieldValue(
																fieldName('applicationLetterFile'),
															)?.name || '',
														status: 'done' as const,
														url: applicationLetterFile ? '#' : undefined,
													},
												]
											: []
									}
									showUploadList={{
										showRemoveIcon: true,
										showPreviewIcon: true,
									}}
									// eslint-disable-next-line @typescript-eslint/no-unused-vars
									onPreview={(_file) => {
										if (applicationLetterFile) {
											openPreviewModal(applicationLetterFile);
										}
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = `customer/${data?.customerId}/pdp-form`;

										try {
											await uploadApplicationLetterMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											const payload: SubmitMPPSlikRequestFilesPayload['applicationLetterFile'] =
												{
													id: fileid,
													name: file.name,
													path: filepath,
												};

											form.setFieldValue(
												fieldName('applicationLetterFile'),
												payload,
											);
											updateFileStatus();
										} catch {
											form.setFieldValue(
												fieldName('applicationLetterFile'),
												null,
											);
											updateFileStatus();
										}
									}}
									onRemove={() => {
										form.setFieldValue(
											fieldName('applicationLetterFile'),
											null,
										);
										updateFileStatus();
										return false;
									}}
								>
									{uploadApplicationLetterMutation.isPending ? (
										<LoadingOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									) : (
										<>
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
											<div
												style={{
													fontSize: 12,
													marginTop: 10,
													color: '#BABABA',
												}}
											>
												{form.getFieldValue(fieldName('applicationLetterFile'))
													? 'Upload to replace current file'
													: applicationLetterFile
														? 'Upload to replace current file'
														: 'Silakan Upload Surat Permohonan Anda'}
											</div>
										</>
									)}
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</div>
		</Modal>
	);
};

export default UploadSLIKModal;
